import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { overviewUrl } from "../../BaseURL";

// Dot color per activity category.  Anything not listed falls back to mid.
const DOT_COLOR = {
  room:       "#c8a05a",
  room_type:  "#c8a05a",
  user:       "#2980b9",
  auth:       "#4a7c59",
  billing:    "#2d6a4f",
  guest:      "#2980b9",
  system:     "#8a7060",
  alert:      "#c0392b",
  // Mock-era keys kept so older fixture entries still render in colour.
  checkin:    "#4a7c59",
  checkout:   "#c8a05a",
  booking:    "#2980b9",
  payment:    "#2d6a4f",
  task:       "#8a7060",
};

// Convert a timestamp into a short, human-friendly label for the feed.
//   today      → "09:42 AM"
//   yesterday  → "Yesterday · 4:15 PM"
//   older      → "Apr 8 · 4:15 PM"
function fmtWhen(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay)     return time;
  if (isYesterday) return `Yesterday · ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${time}`;
}

export default function Overview({ isAdmin, setActiveTab }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const res = await axios.get(overviewUrl);
      if (res.data?.success) setData(res.data);
      else setErr(res.data?.message || "Could not load overview.");
    } catch (e) {
      console.error("overview load:", e);
      setErr(e.response?.data?.message || "Could not load overview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Soft auto-refresh keeps the feed alive without a manual reload.  30s
  // is gentle on the DB and matches the rhythm a front-desk operator
  // would expect.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const rooms     = data?.rooms || { total:0, by_status:{}, occupancy_rate:0, occupied:0 };
  const occupied  = rooms.occupied || 0;
  const total     = rooms.total || 0;
  const occupancy = rooms.occupancy_rate || 0;
  const available = rooms.by_status?.available || 0;
  const reserved  = rooms.by_status?.reserved  || 0;
  const cleaning  = rooms.by_status?.cleaning  || 0;
  const maint     = (rooms.by_status?.maintenance || 0) + (rooms.by_status?.out_of_order || 0);

  const activity  = data?.activity || [];

  // Pending = real data we don't have a module for yet.  Render with the
  // existing card design but show "—" and a softly-styled subtext so it's
  // obviously a placeholder, not a fake metric.
  const stats = [
    {
      icon: "🏨",
      val:  loading ? "…" : `${occupied}/${total}`,
      lbl:  "Occupied Rooms",
      sub:  loading ? "Loading…" : total > 0 ? `${occupancy}% occupancy rate` : "No rooms registered yet",
      warn: false,
    },
    {
      icon: "👥",
      val:  "—",
      lbl:  "Active Guests",
      sub:  "Wired once Guests module ships",
      pending: true,
    },
    {
      icon: "📋",
      val:  "—",
      lbl:  "Confirmed Bookings",
      sub:  "Wired once Reservations ships",
      pending: true,
    },
    {
      icon: "💰",
      val:  "—",
      lbl:  "Outstanding",
      sub:  "Wired once Billing ships",
      pending: true,
    },
  ];

  const circumference = 2 * Math.PI * 52;

  return (<>
    {isAdmin && (
      <div className="admin-banner">
        <div>
          <div className="admin-banner-txt">System Administrator</div>
          <div className="admin-banner-sub">Full system access — all modules active</div>
        </div>
        <button className="btn-gold" onClick={() => setActiveTab("users")}>Manage Users</button>
      </div>
    )}

    {err && (
      <div style={{
        marginBottom:16, padding:"10px 14px", borderRadius:8,
        background:"rgba(192,57,43,.07)", color:"#a93226",
        border:"1px solid rgba(192,57,43,.2)", fontSize:13,
      }}>
        {err}
      </div>
    )}

    {/* Stats */}
    <div className="g4">
      {stats.map((s, i) => (
        <div className="stat" key={i} style={{
          animationDelay:`${i * 0.06}s`,
          opacity: s.pending ? .72 : 1,
        }}>
          <div className="stat-icon">{s.icon}</div>
          <div className="stat-val">{s.val}</div>
          <div className="stat-lbl">{s.lbl}</div>
          <div className={`stat-sub ${s.warn ? "stat-warn" : ""}`}
               style={s.pending ? { fontStyle:"italic", color:"#a89486" } : undefined}>
            {s.sub}
          </div>
        </div>
      ))}
    </div>

    {/* Main content */}
    <div className="g21">

      {/* Activity Feed */}
      <div className="panel">
        <div className="panel-hd">
          <span className="panel-hd-title">Live Activity Feed</span>
          <span className="badge badge-gold">{activity.length} recent</span>
        </div>
        {loading ? (
          <div style={{ padding:"40px 20px", textAlign:"center", color:"var(--mid)", fontSize:13 }}>
            Loading activity…
          </div>
        ) : activity.length === 0 ? (
          <div style={{ padding:"40px 20px", textAlign:"center", color:"var(--mid)", fontSize:13 }}>
            No activity recorded yet. As soon as users log in, create rooms, or change room status, the feed will fill up here.
          </div>
        ) : (
          activity.map((a) => (
            <div key={a.id} style={{
              display:"flex", alignItems:"flex-start", gap:12,
              padding:"11px 18px", borderBottom:"1px solid rgba(180,140,80,.07)",
            }}>
              <div style={{
                width:7, height:7, borderRadius:"50%",
                background: DOT_COLOR[a.category] || "#8a7060",
                flexShrink:0, marginTop:5,
              }} />
              <div style={{ fontSize:10, color:"#b8a090", fontWeight:300, flexShrink:0, width:90 }}>
                {fmtWhen(a.created_at)}
              </div>
              <div style={{ fontSize:12.5, color:"#4a3828", fontWeight:300, flex:1 }}>
                {a.message}
                {a.actor_name && (
                  <span style={{ color:"#a89486", marginLeft:6, fontSize:11 }}>
                    · {a.actor_name}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Right column */}
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

        {/* Occupancy Ring */}
        <div className="panel">
          <div className="panel-hd"><span className="panel-hd-title">Occupancy Rate</span></div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
            <div style={{ position:"relative", width:120, height:120 }}>
              <svg viewBox="0 0 120 120" width="120" height="120" style={{ transform:"rotate(-90deg)" }}>
                <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(200,160,90,.12)" strokeWidth="10"/>
                <circle cx="60" cy="60" r="52" fill="none" stroke="#c8a05a" strokeWidth="10"
                  strokeDasharray={`${circumference * occupancy / 100} ${circumference}`}
                  strokeLinecap="round"/>
              </svg>
              <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:300, color:"#2c1f14" }}>
                  {total > 0 ? `${occupancy}%` : "—"}
                </span>
                <span style={{ fontSize:9.5, letterSpacing:".12em", textTransform:"uppercase", color:"#8a7060" }}>
                  Occupied
                </span>
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:10, padding:"0 16px 16px", justifyContent:"center", flexWrap:"wrap" }}>
            {[
              ["Occupied",  occupied,  "#2c1f14"],
              ["Available", available, "rgba(74,124,89,.45)"],
              ["Reserved",  reserved,  "rgba(41,128,185,.45)"],
              ["Cleaning",  cleaning,  "rgba(212,160,23,.45)"],
              ["Maint.",    maint,     "rgba(192,57,43,.45)"],
            ].map(([l,v,c])=>(
              <div key={l} style={{ textAlign:"center", minWidth:48 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:c, margin:"0 auto 4px" }}/>
                <div style={{ fontSize:11, color:"#8a7060", fontWeight:300 }}>{l}</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:"#2c1f14" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="panel">
          <div className="panel-hd"><span className="panel-hd-title">Quick Actions</span></div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:14 }}>
            {[
              ["🏨","Rooms",        "rooms"],
              ["🔑","Check In",     "checkinout"],
              ["🚪","Check Out",    "checkinout"],
              ["📋","New Booking",  "bookings"],
            ].map(([ic, lb, tab]) => (
              <button key={lb} onClick={() => setActiveTab(tab)}
                style={{ padding:"12px 8px", borderRadius:7, border:"1.5px solid rgba(180,140,80,.18)", background:"transparent", cursor:"pointer", fontSize:12, fontFamily:"'Jost',sans-serif", color:"#4a3828", display:"flex", flexDirection:"column", alignItems:"center", gap:6, transition:"all .2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor="#c8a05a"}
                onMouseLeave={e => e.currentTarget.style.borderColor="rgba(180,140,80,.18)"}
              >
                <span style={{ fontSize:20 }}>{ic}</span>{lb}
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  </>);
}
